import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';
import {
  AutoAwesome as GeneratorIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Business as BusinessIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Replay as RetryIcon,
  SwapHoriz as SwapIcon
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import {
  getKnowledgeGeneratorTasks,
  getKnowledgeGeneratorTask,
  createKnowledgeGeneratorTask,
  updateKnowledgeGeneratorSuggestions,
  syncKnowledgeGeneratorTask,
  retryKnowledgeGeneratorTask,
  deleteKnowledgeGeneratorTask,
  type KnowledgeGeneratorTask,
  type KnowledgeGeneratorSuggestion
} from '../services/apiService';

type TaskStatus = 'pending' | 'researching' | 'generating' | 'ready_for_review' | 'syncing' | 'completed' | 'error';

const KnowledgeGenerator: React.FC = () => {
  // State
  const [tasks, setTasks] = useState<KnowledgeGeneratorTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<KnowledgeGeneratorTask | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTaskDetail, setLoadingTaskDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Create task form
  const [companyName, setCompanyName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit suggestion state
  const [editingSuggestion, setEditingSuggestion] = useState<{
    suggestion: KnowledgeGeneratorSuggestion;
    type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq';
  } | null>(null);
  const [editForm, setEditForm] = useState({
    question: '',
    answer: '',
    category: '',
    variations: [] as { tempId: string; question: string }[]
  });

  // Sync state
  const [syncing, setSyncing] = useState(false);

  // Delete state
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);

  // Tab state for detail view
  const [activeTab, setActiveTab] = useState<'objections' | 'faqs'>('objections');
  const [faqSubTab, setFaqSubTab] = useState<'website' | 'aiGenerated'>('website');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Polling ref
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Edit modal scroll ref
  const editModalContentRef = useRef<HTMLDivElement>(null);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getKnowledgeGeneratorTasks(pagination.page, pagination.limit);
      setTasks(response.data || []);
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, pages: 1 });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Poll for active tasks
  useEffect(() => {
    const hasActiveTasks = tasks.some(task =>
      ['pending', 'researching', 'generating', 'syncing'].includes(task.status)
    );

    if (hasActiveTasks && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        fetchTasks();
      }, 5000);
    } else if (!hasActiveTasks && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [tasks, fetchTasks]);

  // Create task handler
  const handleCreateTask = async () => {
    if (!companyName.trim() || !websiteUrl.trim() || !companyDescription.trim()) {
      setCreateError('Company name, website URL, and company description are required');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      // Send URL as-is, backend will normalize it (add https:// if needed)
      await createKnowledgeGeneratorTask(
        companyName.trim(),
        websiteUrl.trim(),
        companyDescription.trim()
      );
      setSuccess('Task created successfully! Processing will begin shortly.');
      setShowCreateModal(false);
      setCompanyName('');
      setWebsiteUrl('');
      setCompanyDescription('');
      setCreateError(null);
      fetchTasks();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      // Extract user-friendly error message from API response
      const errorMessage = err?.error || err?.message || 'Failed to create task';
      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  // View task details
  const handleViewTask = async (taskId: string) => {
    try {
      setLoadingTaskDetail(true);
      const task = await getKnowledgeGeneratorTask(taskId);
      setSelectedTask(task);
      setShowDetailView(true);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch task details');
    } finally {
      setLoadingTaskDetail(false);
    }
  };

  // Retry failed task
  const handleRetryTask = async (taskId: string) => {
    try {
      await retryKnowledgeGeneratorTask(taskId);
      setSuccess('Task queued for retry');
      fetchTasks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to retry task');
    }
  };

  // Delete task
  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      setDeletingTaskId(taskId);
      await deleteKnowledgeGeneratorTask(taskId);
      setSuccess('Task deleted successfully');
      fetchTasks();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete task');
    } finally {
      setDeletingTaskId(null);
    }
  };

  // Toggle suggestion approval
  const handleToggleApproval = (tempId: string, type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq') => {
    if (!selectedTask?.suggestions) return;

    let suggestions: KnowledgeGeneratorSuggestion[];
    let key: string;

    if (type === 'objection') {
      suggestions = selectedTask.suggestions.objections || [];
      key = 'objections';
    } else if (type === 'websiteFaq') {
      suggestions = selectedTask.suggestions.websiteFaqs || [];
      key = 'websiteFaqs';
    } else {
      suggestions = selectedTask.suggestions.aiGeneratedFaqs || [];
      key = 'aiGeneratedFaqs';
    }

    const updatedSuggestions = suggestions.map(s =>
      s.tempId === tempId ? { ...s, approved: !s.approved } : s
    );

    setSelectedTask({
      ...selectedTask,
      suggestions: {
        ...selectedTask.suggestions,
        [key]: updatedSuggestions
      }
    });
  };

  // Open edit modal
  const handleEditSuggestion = (suggestion: KnowledgeGeneratorSuggestion, type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq') => {
    setEditingSuggestion({ suggestion, type });
    setEditForm({
      question: suggestion.question,
      answer: suggestion.answer,
      category: suggestion.category,
      variations: suggestion.variations || []
    });
    setShowEditModal(true);
  };

  // Save edited suggestion
  const handleSaveEdit = () => {
    if (!selectedTask?.suggestions || !editingSuggestion) return;

    const { type } = editingSuggestion;
    let suggestions: KnowledgeGeneratorSuggestion[];
    let key: string;

    if (type === 'objection') {
      suggestions = selectedTask.suggestions.objections || [];
      key = 'objections';
    } else if (type === 'websiteFaq') {
      suggestions = selectedTask.suggestions.websiteFaqs || [];
      key = 'websiteFaqs';
    } else {
      suggestions = selectedTask.suggestions.aiGeneratedFaqs || [];
      key = 'aiGeneratedFaqs';
    }

    const updatedSuggestions = suggestions.map(s =>
      s.tempId === editingSuggestion.suggestion.tempId
        ? {
            ...s,
            question: editForm.question,
            answer: editForm.answer,
            category: editForm.category,
            variations: editForm.variations,
            edited: true
          }
        : s
    );

    setSelectedTask({
      ...selectedTask,
      suggestions: {
        ...selectedTask.suggestions,
        [key]: updatedSuggestions
      }
    });

    setShowEditModal(false);
    setEditingSuggestion(null);
  };

  // Get current suggestions array based on active tab/subtab
  const getCurrentSuggestions = (): { suggestions: KnowledgeGeneratorSuggestion[]; key: string } => {
    if (!selectedTask?.suggestions) return { suggestions: [], key: '' };

    if (activeTab === 'objections') {
      return { suggestions: selectedTask.suggestions.objections || [], key: 'objections' };
    } else if (faqSubTab === 'website') {
      return { suggestions: selectedTask.suggestions.websiteFaqs || [], key: 'websiteFaqs' };
    } else {
      return { suggestions: selectedTask.suggestions.aiGeneratedFaqs || [], key: 'aiGeneratedFaqs' };
    }
  };

  // Check all unsynced suggestions in current tab
  const handleCheckAll = () => {
    if (!selectedTask?.suggestions) return;

    const { suggestions, key } = getCurrentSuggestions();

    const updatedSuggestions = suggestions.map(s =>
      s.synced ? s : { ...s, approved: true }
    );

    setSelectedTask({
      ...selectedTask,
      suggestions: {
        ...selectedTask.suggestions,
        [key]: updatedSuggestions
      }
    });
  };

  // Uncheck all unsynced suggestions in current tab
  const handleUncheckAll = () => {
    if (!selectedTask?.suggestions) return;

    const { suggestions, key } = getCurrentSuggestions();

    const updatedSuggestions = suggestions.map(s =>
      s.synced ? s : { ...s, approved: false }
    );

    setSelectedTask({
      ...selectedTask,
      suggestions: {
        ...selectedTask.suggestions,
        [key]: updatedSuggestions
      }
    });
  };

  // Move suggestion between objections and FAQs
  const handleMoveSuggestion = (suggestion: KnowledgeGeneratorSuggestion, fromType: 'objection' | 'websiteFaq' | 'aiGeneratedFaq') => {
    if (!selectedTask?.suggestions) return;

    // Determine target type - moving to/from objections goes to aiGeneratedFaqs
    const toType = fromType === 'objection' ? 'aiGeneratedFaq' : 'objection';

    // Get source list and key
    let sourceList: KnowledgeGeneratorSuggestion[];
    let sourceKey: string;
    if (fromType === 'objection') {
      sourceList = selectedTask.suggestions.objections || [];
      sourceKey = 'objections';
    } else if (fromType === 'websiteFaq') {
      sourceList = selectedTask.suggestions.websiteFaqs || [];
      sourceKey = 'websiteFaqs';
    } else {
      sourceList = selectedTask.suggestions.aiGeneratedFaqs || [];
      sourceKey = 'aiGeneratedFaqs';
    }

    // Get destination list and key
    let destList: KnowledgeGeneratorSuggestion[];
    let destKey: string;
    if (toType === 'objection') {
      destList = selectedTask.suggestions.objections || [];
      destKey = 'objections';
    } else {
      destList = selectedTask.suggestions.aiGeneratedFaqs || [];
      destKey = 'aiGeneratedFaqs';
    }

    // Remove from source list
    const updatedSource = sourceList.filter(s => s.tempId !== suggestion.tempId);

    // Add to destination list
    const updatedDest = [...destList, { ...suggestion, edited: true }];

    setSelectedTask({
      ...selectedTask,
      suggestions: {
        ...selectedTask.suggestions,
        [sourceKey]: updatedSource,
        [destKey]: updatedDest
      }
    });
  };

  // Sync directly to knowledge base (without duplicate check)
  const handleDirectSync = async () => {
    if (!selectedTask) return;

    // Check if there are any unsynced approved items to sync
    const unsyncedObjections = selectedTask.suggestions?.objections?.filter(s => s.approved && !s.synced) || [];
    const unsyncedWebsiteFaqs = selectedTask.suggestions?.websiteFaqs?.filter(s => s.approved && !s.synced) || [];
    const unsyncedAiGeneratedFaqs = selectedTask.suggestions?.aiGeneratedFaqs?.filter(s => s.approved && !s.synced) || [];

    if (unsyncedObjections.length === 0 && unsyncedWebsiteFaqs.length === 0 && unsyncedAiGeneratedFaqs.length === 0) {
      setError('No entries selected to sync. Please check the items you want to sync.');
      return;
    }

    try {
      setSyncing(true);

      // First save current suggestions
      await updateKnowledgeGeneratorSuggestions(selectedTask._id, {
        objections: selectedTask.suggestions?.objections,
        websiteFaqs: selectedTask.suggestions?.websiteFaqs,
        aiGeneratedFaqs: selectedTask.suggestions?.aiGeneratedFaqs
      });

      // Get tempIds for all approved unsynced items
      const objectionTempIds = unsyncedObjections.map(s => s.tempId);
      const websiteFaqTempIds = unsyncedWebsiteFaqs.map(s => s.tempId);
      const aiGeneratedFaqTempIds = unsyncedAiGeneratedFaqs.map(s => s.tempId);

      // Sync directly - combine all FAQ IDs for the API call
      const allFaqTempIds = [...websiteFaqTempIds, ...aiGeneratedFaqTempIds];
      const results = await syncKnowledgeGeneratorTask(
        selectedTask._id,
        objectionTempIds,
        allFaqTempIds
      );

      const syncedCount = (results.totalObjections || 0) + (results.totalFaqs || 0);

      if (results.taskCompleted) {
        // All items synced, task completed
        setSuccess(`Successfully synced ${results.totalObjections} objections and ${results.totalFaqs} FAQs. Task completed!`);
        setShowDetailView(false);
        setSelectedTask(null);
      } else {
        // Partial sync - there are remaining items
        setSuccess(`Synced ${syncedCount} items. ${results.remainingCount || 0} approved items remaining to sync.`);

        // Refresh the selected task to get updated synced status
        const updatedTask = await getKnowledgeGeneratorTask(selectedTask._id);
        setSelectedTask(updatedTask);
      }

      fetchTasks();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to sync to knowledge base');
    } finally {
      setSyncing(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status: TaskStatus) => {
    const badges: Record<TaskStatus, React.ReactElement> = {
      pending: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <PendingIcon style={{ fontSize: '0.875rem' }} />
          Pending
        </span>
      ),
      researching: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <RefreshIcon className="animate-spin" style={{ fontSize: '0.875rem' }} />
          Researching
        </span>
      ),
      generating: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <RefreshIcon className="animate-spin" style={{ fontSize: '0.875rem' }} />
          Generating
        </span>
      ),
      ready_for_review: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <WarningIcon style={{ fontSize: '0.875rem' }} />
          Ready for Review
        </span>
      ),
      syncing: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <SyncIcon className="animate-spin" style={{ fontSize: '0.875rem' }} />
          Syncing
        </span>
      ),
      completed: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon style={{ fontSize: '0.875rem' }} />
          Completed
        </span>
      ),
      error: (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ErrorIcon style={{ fontSize: '0.875rem' }} />
          Error
        </span>
      )
    };
    return badges[status] || <span>{status}</span>;
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  return (
    <MainLayout title="Knowledge Generator">
      <div className="p-2">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Knowledge Generator</h1>
            <p className="mt-1 text-gray-600">
              Generate objections and FAQs from company websites using AI
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <AddIcon style={{ fontSize: '1.25rem' }} />
            Create Task
          </button>
        </div>

        {/* Alerts */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircleIcon className="text-green-600 mr-2" style={{ fontSize: '1.25rem' }} />
            <span className="text-green-800">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-auto text-green-600 hover:text-green-800">
              <CloseIcon style={{ fontSize: '1rem' }} />
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <XCircleIcon className="text-red-600 mr-2" style={{ fontSize: '1.25rem' }} />
            <span className="text-red-800">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <CloseIcon style={{ fontSize: '1rem' }} />
            </button>
          </div>
        )}

        {/* Task List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Tasks</h2>
            <button
              onClick={fetchTasks}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1rem' }} />
              Refresh
            </button>
          </div>

          {loading && tasks.length === 0 ? (
            <div className="p-8 text-center">
              <RefreshIcon className="animate-spin mx-auto text-gray-400" style={{ fontSize: '2rem' }} />
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="p-8 text-center">
              <GeneratorIcon className="mx-auto text-gray-300" style={{ fontSize: '4rem' }} />
              <p className="mt-4 text-gray-500">No tasks found. Create a task to get started.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <AddIcon style={{ fontSize: '1.25rem' }} />
                Create Your First Task
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Company</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Website</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Progress</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Suggested</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Saved</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Created</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <tr
                      key={task._id}
                      className={`hover:bg-gray-50 transition-colors ${['ready_for_review', 'completed'].includes(task.status) ? 'cursor-pointer' : ''}`}
                      onClick={() => ['ready_for_review', 'completed'].includes(task.status) && handleViewTask(task._id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <BusinessIcon className="text-gray-400" style={{ fontSize: '1.25rem' }} />
                          <span className="font-medium text-gray-900">{task.companyName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(task.websiteUrl, '_blank');
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 cursor-pointer"
                        >
                          <LinkIcon style={{ fontSize: '0.875rem' }} />
                          {task.websiteUrl.replace(/^https?:\/\//, '').slice(0, 30)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          {getStatusBadge(task.status)}
                          {task.syncStatus === 'error' && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                              Sync Error
                            </span>
                          )}
                          {task.syncStatus === 'syncing' && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                              Syncing...
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {task.progress?.percentage !== undefined && (
                          <div className="w-24 mx-auto">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-600 transition-all duration-300"
                                style={{ width: `${task.progress.percentage}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{task.progress.percentage}%</p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(task.suggestedObjectionsCount || task.suggestedFaqsCount) ? (
                          <div className="text-xs">
                            <div className="text-orange-600 font-medium">{task.suggestedObjectionsCount || 0} objections</div>
                            <div className="text-blue-600 font-medium">{task.suggestedFaqsCount || 0} FAQs</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(task.syncResults?.totalObjections || task.syncResults?.totalFaqs) ? (
                          <div className="text-xs">
                            <div className="text-orange-600 font-medium">{task.syncResults?.totalObjections || 0} objections</div>
                            <div className="text-blue-600 font-medium">{task.syncResults?.totalFaqs || 0} FAQs</div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(task.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {task.status === 'error' && (
                            <button
                              onClick={() => handleRetryTask(task._id)}
                              className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                              title="Retry"
                            >
                              <RetryIcon style={{ fontSize: '1.25rem' }} />
                            </button>
                          )}
                          {['pending', 'error', 'completed', 'ready_for_review'].includes(task.status) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTask(task._id);
                              }}
                              disabled={deletingTaskId === task._id}
                              className={`p-1.5 rounded transition-colors ${deletingTaskId === task._id ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                              title="Delete"
                            >
                              {deletingTaskId === task._id ? (
                                <RefreshIcon className="animate-spin" style={{ fontSize: '1.25rem' }} />
                              ) : (
                                <DeleteIcon style={{ fontSize: '1.25rem' }} />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 rounded text-sm ${pagination.page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className={`px-3 py-1 rounded text-sm ${pagination.page === pagination.pages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create Task Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create Knowledge Generator Task</h3>
                <button onClick={() => { setShowCreateModal(false); setCreateError(null); }} className="text-gray-400 hover:text-gray-600">
                  <CloseIcon />
                </button>
              </div>
              <div className="p-4 space-y-4">
                {/* Error display inside modal */}
                {createError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <XCircleIcon className="text-red-500 flex-shrink-0 mt-0.5" style={{ fontSize: '1.25rem' }} />
                    <div className="flex-1">
                      <p className="text-sm text-red-800">{createError}</p>
                    </div>
                    <button onClick={() => setCreateError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                      <CloseIcon style={{ fontSize: '1rem' }} />
                    </button>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <div className="relative">
                    <BusinessIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: '1.25rem' }} />
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => { setCompanyName(e.target.value); setCreateError(null); }}
                      placeholder="e.g., Acme Corporation"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website URL *
                  </label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: '1.25rem' }} />
                    <input
                      type="text"
                      value={websiteUrl}
                      onChange={(e) => { setWebsiteUrl(e.target.value); setCreateError(null); }}
                      placeholder="e.g., https://example.com"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Description *
                  </label>
                  <textarea
                    value={companyDescription}
                    onChange={(e) => {
                      if (e.target.value.length <= 2000) {
                        setCompanyDescription(e.target.value);
                      }
                    }}
                    placeholder="Describe what the company does, their main products/services, target customers, unique selling points, and anything else that might help the AI"
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <div className="flex justify-end mt-1">
                    <span className={`text-xs ${companyDescription.length >= 1800 ? 'text-orange-500' : 'text-gray-400'}`}>
                      {companyDescription.length}/2000
                    </span>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <InfoIcon className="text-blue-600 mt-0.5" style={{ fontSize: '1.25rem' }} />
                    <div className="text-sm text-blue-800">
                      <p>The system will:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Research the company using the website</li>
                        <li>Generate objections and FAQs</li>
                        <li>Auto-categorize each item</li>
                        <li>Generate 5 variations per item</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => { setShowCreateModal(false); setCreateError(null); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTask}
                  disabled={creating || !companyName.trim() || !websiteUrl.trim() || !companyDescription.trim()}
                  className={`px-4 py-2 rounded-lg transition-colors ${creating || !companyName.trim() || !websiteUrl.trim() || !companyDescription.trim() ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                >
                  {creating ? (
                    <span className="flex items-center gap-2">
                      <RefreshIcon className="animate-spin" style={{ fontSize: '1rem' }} />
                      Creating...
                    </span>
                  ) : (
                    'Create Task'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail View Modal */}
        {showDetailView && selectedTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
            <div className="bg-white rounded-lg shadow-xl flex flex-col" style={{ width: '80vw', height: '92vh', overflow: 'hidden' }}>
              <div className="p-4 border-b border-gray-200 flex items-start justify-between flex-shrink-0">
                <div className="flex-1 min-w-0 pr-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Review Suggestions - {selectedTask.companyName}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedTask.websiteUrl}</p>
                  {selectedTask.companyDescription && (
                    <div className="mt-1 text-sm text-gray-600">
                      <span className="font-bold">Company description: </span>
                      <span className="text-gray-500">
                        {isDescriptionExpanded
                          ? selectedTask.companyDescription
                          : selectedTask.companyDescription.slice(0, 100) + (selectedTask.companyDescription.length > 100 ? '…' : '')}
                      </span>
                      {selectedTask.companyDescription.length > 100 && (
                        <button
                          onClick={() => setIsDescriptionExpanded(prev => !prev)}
                          className="ml-1 text-blue-600 hover:text-blue-700 text-xs font-medium"
                        >
                          {isDescriptionExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={() => { setShowDetailView(false); setSelectedTask(null); setIsDescriptionExpanded(false); }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                  <CloseIcon />
                </button>
              </div>

              {/* Sync Error Banner */}
              {selectedTask.syncStatus === 'error' && selectedTask.syncError && (
                <div className="p-3 bg-red-50 border-b border-red-200 flex-shrink-0">
                  <div className="flex items-start gap-2">
                    <ErrorIcon className="text-red-600 mt-0.5" style={{ fontSize: '1.25rem' }} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">Sync Failed</p>
                      <p className="text-sm text-red-700">{selectedTask.syncError.message}</p>
                      <p className="text-xs text-red-500 mt-1">You can try syncing again after reviewing the items.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tabs */}
              <div className="border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex">
                    <button
                      onClick={() => setActiveTab('objections')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'objections' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                      Objections ({selectedTask.suggestions?.objections?.length || 0})
                    </button>
                    <button
                      onClick={() => setActiveTab('faqs')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'faqs' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                      FAQs ({(selectedTask.suggestions?.websiteFaqs?.length || 0) + (selectedTask.suggestions?.aiGeneratedFaqs?.length || 0)})
                    </button>
                  </div>
                  {/* Check All / Uncheck All buttons */}
                  <div className="flex items-center gap-2 pr-4">
                    <button
                      onClick={handleCheckAll}
                      className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded transition-colors"
                    >
                      Check All
                    </button>
                    <button
                      onClick={handleUncheckAll}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                    >
                      Uncheck All
                    </button>
                  </div>
                </div>
              </div>

              {/* FAQ Sub-tabs (only show when FAQs tab is active) */}
              {activeTab === 'faqs' && (
                <div className="bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <div className="flex px-4">
                    <button
                      onClick={() => setFaqSubTab('website')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${faqSubTab === 'website' ? 'text-blue-600 bg-white border-t border-l border-r border-gray-200 rounded-t -mb-px' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Website FAQs ({selectedTask.suggestions?.websiteFaqs?.length || 0})
                    </button>
                    <button
                      onClick={() => setFaqSubTab('aiGenerated')}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${faqSubTab === 'aiGenerated' ? 'text-blue-600 bg-white border-t border-l border-r border-gray-200 rounded-t -mb-px' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      AI Generated FAQs ({selectedTask.suggestions?.aiGeneratedFaqs?.length || 0})
                    </button>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {(() => {
                  // Get the current suggestions based on active tab and sub-tab
                  let currentSuggestions: KnowledgeGeneratorSuggestion[] = [];
                  let currentType: 'objection' | 'websiteFaq' | 'aiGeneratedFaq' = 'objection';

                  if (activeTab === 'objections') {
                    currentSuggestions = selectedTask.suggestions?.objections || [];
                    currentType = 'objection';
                  } else if (faqSubTab === 'website') {
                    currentSuggestions = selectedTask.suggestions?.websiteFaqs || [];
                    currentType = 'websiteFaq';
                  } else {
                    currentSuggestions = selectedTask.suggestions?.aiGeneratedFaqs || [];
                    currentType = 'aiGeneratedFaq';
                  }

                  return currentSuggestions.map((suggestion) => (
                  <div
                    key={suggestion.tempId}
                    className={`mb-4 p-4 rounded-lg border ${
                      suggestion.synced
                        ? 'border-gray-200 bg-gray-50 opacity-60'
                        : suggestion.approved
                          ? 'border-gray-200 bg-white'
                          : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox on the left */}
                      <div className="pt-1">
                        {suggestion.synced ? (
                          <div className="relative group">
                            <input
                              type="checkbox"
                              checked={true}
                              disabled
                              className="w-5 h-5 text-green-600 rounded border-gray-300 cursor-not-allowed opacity-60"
                            />
                            <div className="absolute left-6 top-0 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                              Already synced to knowledge base
                            </div>
                          </div>
                        ) : (
                          <input
                            type="checkbox"
                            checked={suggestion.approved}
                            onChange={() => handleToggleApproval(suggestion.tempId, currentType)}
                            className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                            {suggestion.category}
                          </span>
                          {currentType === 'websiteFaq' && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                              From Website
                            </span>
                          )}
                          {suggestion.synced && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                              Synced
                            </span>
                          )}
                          {suggestion.edited && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                              Edited
                            </span>
                          )}
                        </div>
                        <p className={`font-medium mb-1 ${suggestion.synced ? 'text-gray-500' : suggestion.approved ? 'text-gray-900' : 'text-gray-500'}`}>{suggestion.question}</p>
                        <p className={`text-sm mb-2 whitespace-pre-wrap ${suggestion.synced ? 'text-gray-400' : suggestion.approved ? 'text-gray-600' : 'text-gray-400'}`}>{suggestion.answer}</p>
                        {suggestion.variations && suggestion.variations.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Variations ({suggestion.variations.length}):</p>
                            <div className="flex flex-wrap gap-1">
                              {suggestion.variations.slice(0, 3).map((v) => (
                                <span key={v.tempId} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  {v.question.slice(0, 40)}...
                                </span>
                              ))}
                              {suggestion.variations.length > 3 && (
                                <span className="text-xs text-gray-400">+{suggestion.variations.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Action buttons on the right */}
                      {!suggestion.synced && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleMoveSuggestion(suggestion, currentType)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title={`Move to ${currentType === 'objection' ? 'AI Generated FAQs' : 'Objections'}`}
                          >
                            <SwapIcon style={{ fontSize: '1.25rem' }} />
                          </button>
                          <button
                            onClick={() => handleEditSuggestion(suggestion, currentType)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <EditIcon style={{ fontSize: '1.25rem' }} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ));
                })()}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="text-sm text-gray-600">
                  <div>
                    <span className="font-medium">
                      {(selectedTask.suggestions?.objections?.filter(s => s.approved && !s.synced).length || 0)} objections,{' '}
                      {(selectedTask.suggestions?.websiteFaqs?.filter(s => s.approved && !s.synced).length || 0)} website FAQs,{' '}
                      {(selectedTask.suggestions?.aiGeneratedFaqs?.filter(s => s.approved && !s.synced).length || 0)} AI generated FAQs
                    </span>
                    {' '}ready to sync
                  </div>
                  {((selectedTask.suggestions?.objections?.filter(s => s.synced).length || 0) +
                    (selectedTask.suggestions?.websiteFaqs?.filter(s => s.synced).length || 0) +
                    (selectedTask.suggestions?.aiGeneratedFaqs?.filter(s => s.synced).length || 0)) > 0 && (
                    <div className="text-green-600 mt-1">
                      {(selectedTask.suggestions?.objections?.filter(s => s.synced).length || 0)} objections,{' '}
                      {(selectedTask.suggestions?.websiteFaqs?.filter(s => s.synced).length || 0)} website FAQs,{' '}
                      {(selectedTask.suggestions?.aiGeneratedFaqs?.filter(s => s.synced).length || 0)} AI generated FAQs already synced
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowDetailView(false); setSelectedTask(null); setIsDescriptionExpanded(false); }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleDirectSync}
                    disabled={syncing || (
                      (selectedTask.suggestions?.objections?.filter(s => s.approved && !s.synced).length || 0) +
                      (selectedTask.suggestions?.websiteFaqs?.filter(s => s.approved && !s.synced).length || 0) +
                      (selectedTask.suggestions?.aiGeneratedFaqs?.filter(s => s.approved && !s.synced).length || 0)
                    ) === 0}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      syncing || (
                        (selectedTask.suggestions?.objections?.filter(s => s.approved && !s.synced).length || 0) +
                        (selectedTask.suggestions?.websiteFaqs?.filter(s => s.approved && !s.synced).length || 0) +
                        (selectedTask.suggestions?.aiGeneratedFaqs?.filter(s => s.approved && !s.synced).length || 0)
                      ) === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {syncing ? (
                      <>
                        <RefreshIcon className="animate-spin" style={{ fontSize: '1rem' }} />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <SyncIcon style={{ fontSize: '1rem' }} />
                        Sync to Knowledge Base
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Suggestion Modal */}
        {showEditModal && editingSuggestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">Edit Suggestion</h3>
                <button onClick={() => { setShowEditModal(false); setEditingSuggestion(null); }} className="text-gray-400 hover:text-gray-600">
                  <CloseIcon />
                </button>
              </div>
              <div ref={editModalContentRef} className="p-4 space-y-4 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                  <textarea
                    value={editForm.question}
                    onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                  <textarea
                    value={editForm.answer}
                    onChange={(e) => setEditForm({ ...editForm, answer: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {['authenticity', 'effectiveness', 'pricing', 'safety', 'implementation', 'durability', 'guarantee', 'product_characteristics', 'technical', 'application', 'skepticism', 'environmental', 'other'].map(cat => (
                      <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                {/* Variations section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Variations ({editForm.variations.length})
                  </label>
                  <div className="space-y-2 border border-gray-200 rounded-lg p-2">
                    {editForm.variations.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-2">No variations</p>
                    ) : (
                      editForm.variations.map((variation, index) => (
                        <div key={variation.tempId} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={variation.question}
                            onChange={(e) => {
                              const updated = [...editForm.variations];
                              updated[index] = { ...updated[index], question: e.target.value };
                              setEditForm({ ...editForm, variations: updated });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Variation text"
                          />
                          <button
                            onClick={() => {
                              const updated = editForm.variations.filter((_, i) => i !== index);
                              setEditForm({ ...editForm, variations: updated });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Remove variation"
                          >
                            <DeleteIcon style={{ fontSize: '1rem' }} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const newVariation = { tempId: `new-${Date.now()}`, question: '' };
                      setEditForm({ ...editForm, variations: [...editForm.variations, newVariation] });
                      // Scroll to bottom after adding variation
                      setTimeout(() => {
                        if (editModalContentRef.current) {
                          editModalContentRef.current.scrollTo({
                            top: editModalContentRef.current.scrollHeight,
                            behavior: 'smooth'
                          });
                        }
                      }, 50);
                    }}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <AddIcon style={{ fontSize: '1rem' }} />
                    Add Variation
                  </button>
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
                <button
                  onClick={() => { setShowEditModal(false); setEditingSuggestion(null); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading Task Detail Overlay */}
        {loadingTaskDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
            <div className="bg-white rounded-lg shadow-xl p-6 flex flex-col items-center gap-3">
              <RefreshIcon className="animate-spin text-blue-600" style={{ fontSize: '2.5rem' }} />
              <p className="text-gray-700 font-medium">Loading task details...</p>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default KnowledgeGenerator;
